import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import { Users, Target, Shield, Zap, Globe, Code, LineChart, BrainCircuit } from 'lucide-react';

function About() {
  return (
    <div className="min-h-screen bg-theme-bg text-theme-text font-sans selection:bg-teal-600/30">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 px-6 overflow-hidden border-b border-theme-border">
        {/* Background Image */ }
        <div className="absolute inset-0 z-0 bg-[url('/about_us_hero.webp')] bg-cover bg-center bg-no-repeat opacity-25 mix-blend-screen pointer-events-none" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-theme-bg via-theme-bg/60 to-transparent pointer-events-none" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
            <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/10 border border-amber-400/20 text-sm mb-6 shadow-sm backdrop-blur-md"
            >
                <BrainCircuit size={16} style={{ color: 'var(--text-accent-purple)' }} />
                <span className="font-medium tracking-wide" style={{ color: 'var(--text-accent-purple)' }}>The Mind Behind The Machine</span>
            </motion.div>

            <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 leading-tight"
            >
                Empowering the Future of <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-400 to-teal-600">
                    Human Learning
                </span>
            </motion.h1>

            <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-xl max-w-2xl mx-auto"
                style={{ color: 'var(--text-accent-cyan)' }}
            >
                Learnly is on a mission to democratize education. We build tools that help academies, corporate training teams, and independent educators scale their knowledge delivery effortlessly using advanced AI models.
            </motion.p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-24 relative z-10">
        
        {/* Animated Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-24">
            {[
                { number: "1M+", label: "Active Learners", icon: Users, color: "text-teal-600" },
                { number: "50k+", label: "Modules Generated", icon: Code, color: "text-amber-500" },
                { number: "120+", label: "Countries Served", icon: Globe, color: "text-amber-500" },
                { number: "99.9%", label: "Uptime SLA", icon: LineChart, color: "text-emerald-500" }
            ].map((stat, i) => (
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    key={i}
                    className="p-6 rounded-3xl bg-theme-card border border-theme-border flex flex-col items-center justify-center text-center shadow-sm relative overflow-hidden"
                >
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                        <stat.icon size={100} />
                    </div>
                    <stat.icon size={24} className={`${stat.color} mb-4 relative z-10`} />
                    <h3 className="text-4xl md:text-5xl font-extrabold text-theme-text mb-2 relative z-10">{stat.number}</h3>
                    <p className="text-sm font-medium relative z-10" style={{ color: 'var(--text-accent-blue)' }}>{stat.label}</p>
                </motion.div>
            ))}
        </div>

        {/* Bento Grid Values */}
        <div className="mb-24">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Our Core Engine</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                <motion.div 
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    className="md:col-span-2 p-8 md:p-12 rounded-3xl bg-gradient-to-br from-teal-900/20 to-theme-card border border-theme-border relative overflow-hidden"
                >
                    <Target size={40} className="text-teal-500 mb-6" />
                    <h3 className="text-3xl font-bold mb-4">Mission Driven</h3>
                    <p className="text-lg max-w-lg" style={{ color: 'var(--text-accent-cyan)' }}>
                        We obsess over creating the best learning outcomes for our users. Our entire infrastructure is built to strip away administrative overhead so you can focus entirely on coding and teaching.
                    </p>
                    <div className="absolute right-0 bottom-0 w-64 h-64 bg-teal-600/10 rounded-full blur-[100px] pointer-events-none" />
                </motion.div>

                <motion.div 
                    initial={{ x: 20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    className="p-8 md:p-12 rounded-3xl bg-theme-card border border-theme-border flex flex-col justify-center relative overflow-hidden"
                >
                    <Shield size={40} className="text-pink-400 mb-6 relative z-10" />
                    <h3 className="text-2xl font-bold mb-4 relative z-10">Trust & Security</h3>
                    <p className="relative z-10" style={{ color: 'var(--text-accent-green)' }}>
                        Enterprise-grade security built directly into our core architecture. Your curriculum and student data remain entirely encrypted.
                    </p>
                    <div className="absolute right-0 top-0 w-32 h-32 bg-amber-400/10 rounded-full blur-[50px] pointer-events-none" />
                </motion.div>

                <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    className="md:col-span-3 p-8 md:p-16 rounded-3xl bg-gradient-to-tr from-theme-bg via-amber-900/10 to-teal-900/10 border border-theme-border text-center overflow-hidden relative"
                >
                    <div className="absolute inset-0 bg-[url('/about_us_hero.webp')] bg-cover bg-center opacity-5 mix-blend-screen pointer-events-none" />
                    <Zap size={48} className="text-amber-400 mb-6 mx-auto relative z-10" />
                    <h3 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Absolute Innovation</h3>
                    <p className="text-lg md:text-xl max-w-3xl mx-auto relative z-10" style={{ color: 'var(--text-accent-purple)' }}>
                        We're pushing boundaries with AI-assisted learning. Our semantic knowledge engines generate dynamic learning paths, reducing drop-off rates and fundamentally changing what an academy looks like.
                    </p>
                </motion.div>

            </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}

export default About;
